import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, User, Calendar, Mail, Phone, MapPin, Globe, CheckCircle, FileText, IdCard, Home } from "lucide-react";

const ClientProfile: React.FC = () => {
  const navigate = useNavigate();

  // Dados do cliente (em produção viriam de uma API)
  const clientData = {
    nomeCompleto: "João Silva",
    cpf: "123.456.789-00",
    rg: "12.345.678-X",
    dataNascimento: "20/05/1990",
    status: "Ativo",
    email: "joao.silva@email.com",
    telefone: "(11) 98765-4321",
    cep: "01310-100",
    endereco: "Av. Paulista",
    numero: "1578",
    complemento: "Apto 42 - Bloco C",
    bairro: "Bela Vista",
    cidade: "São Paulo",
    uf: "SP",
    pais: "Brasil"
  };

  // Calcular idade a partir da data de nascimento
  const calcularIdade = (dataNascimento: string): number => {
    const [dia, mes, ano] = dataNascimento.split('/').map(Number);
    const hoje = new Date();
    const nascimento = new Date(ano, mes - 1, dia);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mesAtual = hoje.getMonth();
    const diaAtual = hoje.getDate();
    
    if (mesAtual < mes - 1 || (mesAtual === mes - 1 && diaAtual < dia)) {
      idade--;
    }
    
    return idade;
  };

  const idade = calcularIdade(clientData.dataNascimento);

  const handleSolicitarAlteracao = () => {
    alert("Sua solicitação de alteração de dados foi enviada. Nossa equipe entrará em contato em até 48 horas.");
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/client/dashboard")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Meus Dados Cadastrais</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-gray-600" />
              Informações Pessoais
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* 👤 Informações Pessoais - Grid Unificado */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <User className="h-4 w-4" />
                Identificação
              </h3>
              
              {/* Linha 1: Nome Completo (Full Width) */}
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-sm text-gray-600">
                  Nome Completo
                </Label>
                <Input
                  id="nome"
                  type="text"
                  value={clientData.nomeCompleto}
                  readOnly
                  disabled
                  className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                />
              </div>

              {/* Linha 2: CPF | Status da Conta */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cpf" className="text-sm text-gray-600">
                    CPF
                  </Label>
                  <Input
                    id="cpf"
                    type="text"
                    value={clientData.cpf}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm text-gray-600">
                    Status da Conta
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="status"
                      type="text"
                      value={clientData.status}
                      readOnly
                      disabled
                      className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed flex-1"
                    />
                    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      <CheckCircle className="h-3 w-3" />
                      {clientData.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Linha 3: RG | Data de Nascimento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rg" className="text-sm text-gray-600 flex items-center gap-2">
                    <IdCard className="h-3 w-3" />
                    RG
                  </Label>
                  <Input
                    id="rg"
                    type="text"
                    value={clientData.rg}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dataNascimento" className="text-sm text-gray-600 flex items-center gap-2">
                    <Calendar className="h-3 w-3" />
                    Data de Nascimento
                  </Label>
                  <Input
                    id="dataNascimento"
                    type="text"
                    value={`${clientData.dataNascimento} (${idade} anos)`}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* 📍 Localização & Contato */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Localização & Contato
              </h3>
              
              {/* Contato */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-gray-600 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    E-mail
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={clientData.email}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone" className="text-sm text-gray-600 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Telefone
                  </Label>
                  <Input
                    id="telefone"
                    type="text"
                    value={clientData.telefone}
                    readOnly
                    disabled
                    className="bg-gray-50 text-gray-900 font-medium cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Endereço Completo */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Endereço de Correspondência
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                  {/* CEP */}
                  <div className="space-y-2 md:col-span-3">
                    <Label htmlFor="cep" className="text-sm text-gray-600">
                      CEP
                    </Label>
                    <Input
                      id="cep"
                      type="text"
                      value={clientData.cep}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Endereço */}
                  <div className="space-y-2 md:col-span-6">
                    <Label htmlFor="endereco" className="text-sm text-gray-600">
                      Endereço
                    </Label>
                    <Input
                      id="endereco"
                      type="text"
                      value={clientData.endereco}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Número */}
                  <div className="space-y-2 md:col-span-3">
                    <Label htmlFor="numero" className="text-sm text-gray-600">
                      Número
                    </Label>
                    <Input
                      id="numero"
                      type="text"
                      value={clientData.numero}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Bairro */}
                  <div className="space-y-2 md:col-span-4">
                    <Label htmlFor="bairro" className="text-sm text-gray-600">
                      Bairro
                    </Label>
                    <Input
                      id="bairro"
                      type="text"
                      value={clientData.bairro}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Complemento */}
                  <div className="space-y-2 md:col-span-4">
                    <Label htmlFor="complemento" className="text-sm text-gray-600">
                      Complemento
                    </Label>
                    <Input
                      id="complemento"
                      type="text"
                      value={clientData.complemento}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Cidade */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="cidade" className="text-sm text-gray-600">
                      Cidade
                    </Label>
                    <Input
                      id="cidade"
                      type="text"
                      value={clientData.cidade}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* UF */}
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="uf" className="text-sm text-gray-600">
                      UF
                    </Label>
                    <Input
                      id="uf"
                      type="text"
                      value={clientData.uf}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* País */}
                <div className="mt-4">
                  <div className="space-y-2 md:max-w-xs">
                    <Label htmlFor="pais" className="text-sm text-gray-600 flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      País
                    </Label>
                    <Input
                      id="pais"
                      type="text"
                      value={clientData.pais}
                      readOnly
                      disabled
                      className="bg-white text-gray-900 font-medium cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Botão de Ação */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Importante:</strong> Para alterar dados sensíveis como CPF, nome ou e-mail, 
                    é necessário solicitar a alteração através do nosso suporte. Nossa equipe entrará 
                    em contato para validar a solicitação.
                  </span>
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleSolicitarAlteracao}
                className="w-full md:w-auto"
              >
                <FileText className="h-4 w-4 mr-2" />
                Solicitar Alteração de Dados
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientProfile;
